import useFooterStore from "views/hooks/useFooterStore";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { AsyncSelect } from "@neonumy/ant-design";
import { useTranslation } from "react-i18next";
import CardTitle from "components/CardTitle";
import { Card, Col, Form, Row } from "antd";
import { FaListAlt } from "react-icons/fa";
import {
  LIST_BOOKS_QUERY,
  LIST_CLIENTS_QUERY,
  LIST_COUNTERPARTS_QUERY,
} from "queries/centralApi/counterparts";
import { useParams } from "react-router-dom";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import type { FormInstance } from "antd";
import type { AccountingTransferFormValues } from "../utils";

interface CounterpartCycle {
  startDate?: string | null;
  endDate?: string | null;
  limitDate?: string | null;
}

/**
 * A `listBooksPaginated` / `listClientsPaginated` / `listCounterpartsPaginated`
 * row as received by `AsyncSelect`'s `optionsFormat`.
 */
interface CounterpartOptionItem extends Record<string, unknown> {
  code?: string;
  name?: string;
  cycle?: CounterpartCycle | null;
}

interface BillingTransferPropsType {
  onFinish: (values: AccountingTransferFormValues) => void | Promise<void>;
  form: FormInstance<AccountingTransferFormValues>;
}

export default function BillingTransfer(props: BillingTransferPropsType): React.ReactElement {
  const { form, onFinish } = props;

  const { t } = useTranslation();
  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const { companyId } = useParams();
  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };
  const createOption = (item: CounterpartOptionItem) => {
    const cycleInfo = item?.cycle?.startDate
      ? `${dayjs(item.cycle.startDate).format(DATE_FORMAT)} - ${dayjs(item?.cycle?.endDate || item?.cycle?.limitDate).format(DATE_FORMAT)}`
      : "";

    return {
      label: (
        <div>
          <div>{`${item.code} - ${item.name}`}</div>
          {cycleInfo && <small>{cycleInfo}</small>}
        </div>
      ),
      value: item.code,
    };
  };
  const createOptionCounterPart = (item: CounterpartOptionItem) => {
    const cycleInfo = item?.cycle?.startDate
      ? `${dayjs(item.cycle.startDate).format(DATE_FORMAT)} - ${dayjs(item?.cycle?.endDate || item?.cycle?.limitDate).format(DATE_FORMAT)}`
      : "";

    return {
      label: (
        <div>
          <div>{`${item.code} - ${item.name}`}</div>
          {cycleInfo && <small>{cycleInfo}</small>}
        </div>
      ),
      value: item.code,
    };
  };

  return (
    <Form
      name="updateCompany"
      onFinishFailed={onFinishFailed}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      autoComplete="off"
    >
      <Card
        title={<CardTitle icon={<FaListAlt />} title={t("Default Counterparts")} />}
        className="antd-newrai-card"
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item name="sellBookCode" label={t("Sell book")}>
              <AsyncSelect
                optionsFormat={createOption}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listBooksPaginated",
                  query: LIST_BOOKS_QUERY,
                  variables: {
                    filter: {
                      isActive: true,
                      companyId,
                    },
                  },
                }}
                translations={{
                  placeholder: t("Select sell book"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t("Client counterpart")} name="clientCounterpartAccount">
              <AsyncSelect
                optionsFormat={createOptionCounterPart}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listClientsPaginated",
                  query: LIST_CLIENTS_QUERY,
                  variables: {
                    filter: {
                      search: "",
                      isActive: true,
                      companyId,
                    },
                  },
                }}
                translations={{
                  placeholder: t("Select default counterpart"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("Advance payment issued counterpart")}
              name="advancePaymentIssuedCounterpart"
            >
              <AsyncSelect
                optionsFormat={createOptionCounterPart}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listCounterpartsPaginated",
                  query: LIST_COUNTERPARTS_QUERY,
                  variables: {
                    filter: {
                      companyId,
                      isActive: true,
                      search: "",
                    },
                    limit: 100,
                  },
                }}
                translations={{
                  placeholder: t("Select default counterpart"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("Advance payment received counterpart")}
              name="advancePaymentReceivedCounterpart"
            >
              <AsyncSelect
                optionsFormat={createOptionCounterPart}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listCounterpartsPaginated",
                  query: LIST_COUNTERPARTS_QUERY,
                  variables: {
                    filter: {
                      companyId,
                      isActive: true,
                      search: "",
                    },
                    limit: 100,
                  },
                }}
                translations={{
                  placeholder: t("Select default counterpart"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("Advance payment issued tva counterpart")}
              name="advancePaymentIssuedTvaCounterpart"
            >
              <AsyncSelect
                optionsFormat={createOptionCounterPart}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listCounterpartsPaginated",
                  query: LIST_COUNTERPARTS_QUERY,
                  variables: {
                    filter: {
                      companyId,
                      isActive: true,
                      search: "",
                    },
                    limit: 100,
                  },
                }}
                translations={{
                  placeholder: t("Select default counterpart"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("Advance payment received tva counterpart")}
              name="advancePaymentReceivedTvaCounterpart"
            >
              <AsyncSelect
                optionsFormat={createOptionCounterPart}
                hasQueryFilter={true}
                queryOptions={{
                  client: clientCentralApiStrawberry,
                  dataName: "listCounterpartsPaginated",
                  query: LIST_COUNTERPARTS_QUERY,
                  variables: {
                    filter: {
                      companyId,
                      isActive: true,
                      search: "",
                    },
                    limit: 100,
                  },
                }}
                translations={{
                  placeholder: t("Select default counterpart"),
                  loadingMessage: t("Loading..."),
                  noOptionsMessage: t("No Options"),
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Form>
  );
}
